import { createTheme, lighten } from "@mui/material/styles";
import { TypographyOptions } from "@mui/material/styles/createTypography";
import { EmtPaletteOptions, EmtTheme } from "../types";
import { palette } from "./palette";

const theme = createTheme();

const generateTheme = (palette: EmtPaletteOptions): EmtTheme =>
  createTheme(theme, {
    typography: {
      fontFamily: `"Nunito Sans","Helvetica","Arial",sans-serif`,
      h1: {
        fontSize: "3.75rem",
        fontWeight: "800",
      },
      h2: {
        fontSize: "3rem",
        fontWeight: "bold",
      },
      h3: {
        fontSize: "2.125rem",
        fontWeight: "bold",
      },
      h4: {
        fontSize: "1.5rem",
        fontWeight: "semibold",
      },
      h5: {
        fontSize: "1.125rem",
        fontWeight: "bold",
      },
      body1: {
        fontSize: "1rem",
        fontWeight: "regular",
      },
      body2: {
        fontSize: "0.875rem",
        fontWeight: "regular",
      },
      caption: {
        fontSize: "0.75rem",
        fontWeight: "semibold",
      },
    } as TypographyOptions,
    palette: {
      ...palette,
    },
    components: {
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: "inherit",
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            padding: 0,
            "&.Mui-expanded": {
              "& .MuiAccordionSummary-expandIconWrapper": {
                transform: "rotate(90deg)",
              },
            },
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            padding: "0px !important",
            margin: 0,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: palette.info[300],
          },
        },
        variants: [
          {
            props: { variant: "dashed" },
            style: {
              background: "transparent",
              border: `2px dashed`,
              borderColor: palette.greyGradient[100],
              "& .emt-icons": {
                color: palette.greyGradient[550],
                fontSize: "16px !important",
              },
            },
          },
          {
            props: { variant: "roundeddashed" },
            style: {
              background: "transparent",
              border: `2px dashed`,
              borderColor: palette.greyGradient[100],
              "& .emt-icons": {
                color: palette.greyGradient[550],
                fontSize: "16px !important",
              },
            },
          },
          {
            props: { sizes: "large", variant: "roundeddashed" },
            style: {
              borderRadius: 18,
            },
          },
          {
            props: { sizes: "medium", variant: "roundeddashed" },
            style: {
              borderRadius: 18,
            },
          },
          {
            props: { sizes: "small", variant: "roundeddashed" },
            style: {
              borderRadius: 12,
            },
          },
          {
            props: { sizes: "large", variant: "rounded" },
            style: {
              borderRadius: 18,
            },
          },
          {
            props: { sizes: "medium", variant: "rounded" },
            style: {
              borderRadius: 18,
            },
          },
          {
            props: { sizes: "small", variant: "rounded" },
            style: {
              borderRadius: 12,
            },
          },
          {
            props: { sizes: "large" },
            style: {
              width: 64,
              height: 64,
              fontSize: 24,
              "& .emt-icons": {
                fontSize: 30,
              },
            },
          },
          {
            props: { sizes: "medium" },
            style: {
              width: 48,
              height: 48,
              fontSize: 18,
              "& .emt-icons": {
                fontSize: 20,
              },
            },
          },
          {
            props: { sizes: "small" },
            style: {
              width: 32,
              height: 32,
              fontSize: 14,
              "& .emt-icons": {
                fontSize: 13,
              },
            },
          },
        ],
      },
      MuiButtonGroup: {
        styleOverrides: {
          root: {
            boxShadow: "inherit",
            background: "white",
            borderRadius: "12px",
            justifyContent: "space-between",
            "& button": {
              border: "0",
              borderRadius: "12px !important",
              flex: "1",
            },
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: `"Nunito Sans","Helvetica","Arial",sans-serif`,
          },
        },
        variants: [
          {
            props: { color: "white" },
            style: {
              color: palette.common.white,
            },
          },
        ],
      },
      MuiButtonBase: {
        defaultProps: {
          // The props to apply
          disableRipple: true, // No more ripple, on the whole application 💣!
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            borderRadius: "16px",
            textTransform: "none",
            fontWeight: "bold",
            padding: "8px 13px",
            border: `2px solid`,
            fontFamily: `"Nunito Sans","Helvetica","Arial",sans-serif`,
            alignItems: "center",
            lineHeight: "1.55",
          },
          sizeSmall: {
            fontSize: "12px",
            padding: "5px 10px",
            borderRadius: "14px",
            "& .emt-icons": {
              fontSize: "13px !important",
            },
          },
          sizeMedium: {
            fontSize: "14px",
            "& .emt-icons": {
              fontSize: "14px !important",
            },
          },
          sizeLarge: {
            fontSize: "16px",
            "& .emt-icons": {
              fontSize: "16px !important",
            },
          },
        },
        variants: [
          {
            props: { variant: "contained" },
            style: {
              "&:hover": {
                boxShadow: "none",
              },
            },
          },
          {
            props: { variant: "outlined" },
            style: {
              "&:hover": {
                borderWidth: "2px",
                backgroundColor: "transparent",
              },
              "&:disabled": {
                borderWidth: "2px",
              },
            },
          },
          {
            props: { variant: "text" },
            style: {
              border: `2px solid transparent`,
              background: "transparent",
            },
          },

          /**
           * Primary Button
           */
          {
            props: { color: "primary" },
            style: {
              "&:hover": {
                borderColor: palette.primary.light,
              },
            },
          },
          {
            props: { color: "primary", variant: "text" },
            style: {
              color: palette.primary.main,
              "&:hover": {
                borderColor: "transparent",
              },
              "&:disabled": {
                color: palette.primary[150],
              },
            },
          },
          {
            props: { color: "primary", variant: "outlined" },
            style: {
              color: palette.primary.main,
              "&:hover": {
                color: palette.primary.light,
              },
              "&:disabled": {
                color: palette.primary[150],
                borderColor: palette.primary[150],
              },
            },
          },
          {
            props: { color: "primary", variant: "contained" },
            style: {
              color: palette.common.white,
              borderColor: palette.primary.main,
              backgroundColor: palette.primary.main,
              "&:hover": {
                borderColor: palette.primary.light,
                backgroundColor: palette.primary.light,
              },
              "&:disabled": {
                backgroundColor: palette.primary[150],
                color: palette.common.white,
                borderColor: palette.primary[150],
              },
              "&:focus": {
                borderColor: palette.primary[150],
              },
            },
          },
          {
            props: { color: "primary", variant: "whiteOutlined" },
            style: {
              background: palette.common.white,
              color: palette.primary.main,
              "&:hover": {
                background: palette.common.white,
                color: palette.primary.light,
              },
              "&:disabled": {
                color: palette.primary[150],
                borderColor: palette.primary[150],
                background: palette.common.white,
              },
            },
          },

          /**
           * Secondary Button
           */
          {
            props: { color: "secondary" },
            style: {
              "&:hover": {
                borderColor: palette.secondary.light,
              },
            },
          },
          {
            props: { color: "secondary", variant: "text" },
            style: {
              color: palette.secondary.main,
              "&:hover": {
                borderColor: "transparent",
              },
              "&:disabled": {
                color: palette.secondary[150],
              },
            },
          },
          {
            props: { color: "secondary", variant: "outlined" },
            style: {
              color: palette.secondary.main,
              "&:hover": {
                color: palette.secondary.light,
              },
              "&:disabled": {
                color: palette.secondary[150],
                borderColor: palette.secondary[150],
              },
            },
          },
          {
            props: { color: "secondary", variant: "contained" },
            style: {
              color: palette.common.white,
              borderColor: palette.secondary.main,
              backgroundColor: palette.secondary.main,
              "&:hover": {
                borderColor: palette.secondary.light,
                backgroundColor: palette.secondary.light,
              },
              "&:disabled": {
                backgroundColor: palette.secondary[150],
                color: palette.common.white,
                borderColor: palette.secondary[150],
              },
              "&:focus": {
                borderColor: palette.secondary[150],
              },
            },
          },

          /**
           * Success Button
           */
          {
            props: { color: "success" },
            style: {
              "&:hover": {
                borderColor: palette.success[150],
              },
            },
          },
          {
            props: { color: "success", variant: "text" },
            style: {
              color: palette.success.main,
              "&:hover": {
                borderColor: "transparent",
              },
              "&:disabled": {
                color: palette.success[150],
              },
            },
          },
          {
            props: { color: "success", variant: "outlined" },
            style: {
              color: palette.success.main,
              "&:hover": {
                color: palette.success.light,
              },
              "&:disabled": {
                color: palette.success[150],
                borderColor: palette.success[150],
              },
            },
          },
          {
            props: { color: "success", variant: "contained" },
            style: {
              color: palette.common.white,
              borderColor: palette.success.main,
              backgroundColor: palette.success.main,
              "&:hover": {
                borderColor: palette.success.light,
                backgroundColor: palette.success.light,
              },
              "&:disabled": {
                backgroundColor: palette.success[150],
                color: palette.common.white,
                borderColor: palette.success[150],
              },
              "&:focus": {
                borderColor: palette.success[150],
              },
            },
          },

          /**
           * Error Button
           */
          {
            props: { color: "error" },
            style: {
              "&:hover": {
                borderColor: palette.error[150],
              },
            },
          },
          {
            props: { color: "error", variant: "text" },
            style: {
              color: palette.error.main,
              "&:hover": {
                borderColor: "transparent",
              },
              "&:disabled": {
                color: palette.error[150],
              },
            },
          },
          {
            props: { color: "error", variant: "outlined" },
            style: {
              color: palette.error.main,
              "&:hover": {
                color: palette.error.light,
              },
              "&:disabled": {
                color: palette.error[150],
                borderColor: palette.error[150],
              },
            },
          },
          {
            props: { color: "error", variant: "contained" },
            style: {
              color: palette.common.white,
              borderColor: palette.error.main,
              backgroundColor: palette.error.main,
              "&:hover": {
                borderColor: palette.error.light,
                backgroundColor: palette.error.light,
              },
              "&:disabled": {
                backgroundColor: palette.error[150],
                color: palette.common.white,
                borderColor: palette.error[150],
              },
              "&:focus": {
                borderColor: palette.error[150],
              },
            },
          },

          /**
           * Info Button
           */
          {
            props: { color: "info" },
            style: {
              "&:hover": {
                borderColor: palette.info.light,
              },
            },
          },
          {
            props: { color: "info", variant: "text" },
            style: {
              color: palette.info.main,
              "&:hover": {
                borderColor: "transparent",
              },
              "&:disabled": {
                color: palette.info[150],
              },
            },
          },
          {
            props: { color: "info", variant: "outlined" },
            style: {
              color: palette.info.main,
              "&:hover": {
                color: palette.info.light,
              },
              "&:disabled": {
                color: palette.info[150],
                borderColor: palette.info[150],
              },
            },
          },
          {
            props: { color: "info", variant: "contained" },
            style: {
              color: palette.common.white,
              borderColor: palette.info.main,
              backgroundColor: palette.info.main,
              "&:hover": {
                borderColor: palette.info.light,
                backgroundColor: palette.info.light,
              },
              "&:disabled": {
                backgroundColor: palette.info[150],
                color: palette.common.white,
                borderColor: palette.info[150],
              },
              "&:focus": {
                borderColor: palette.info[150],
              },
            },
          },

          /**
           * Warning Button
           */
          {
            props: { color: "warning" },
            style: {
              "&:hover": {
                borderColor: palette.warning.light,
              },
            },
          },
          {
            props: { color: "warning", variant: "text" },
            style: {
              color: palette.warning.main,
              "&:hover": {
                borderColor: "transparent",
              },
              "&:disabled": {
                color: palette.warning[150],
              },
            },
          },
          {
            props: { color: "warning", variant: "outlined" },
            style: {
              color: palette.warning.main,
              "&:hover": {
                color: palette.warning.light,
              },
              "&:disabled": {
                color: palette.warning[150],
                borderColor: palette.warning[150],
              },
            },
          },
          {
            props: { color: "warning", variant: "contained" },
            style: {
              color: palette.common.white,
              borderColor: palette.warning.main,
              backgroundColor: palette.warning.main,
              "&:hover": {
                borderColor: palette.warning.light,
                backgroundColor: palette.warning.light,
              },
              "&:disabled": {
                backgroundColor: palette.warning[150],
                color: palette.common.white,
                borderColor: palette.warning[150],
              },
              "&:focus": {
                borderColor: palette.warning[150],
              },
            },
          },

          /**
           * Green Button
           */
          {
            props: { color: "green" },
            style: {
              "&:hover": {
                borderColor: palette.success[600],
              },
            },
          },
          {
            props: { color: "green", variant: "text" },
            style: {
              color: palette.info.main,
              "&:hover": {
                borderColor: "transparent",
              },
              "&:disabled": {
                color: palette.success[600],
              },
            },
          },
          {
            props: { color: "green", variant: "outlined" },
            style: {
              color: palette.success.main,
              "&:hover": {
                color: palette.success.light,
              },
              "&:disabled": {
                color: palette.success[600],
                borderColor: palette.success[600],
              },
            },
          },
          {
            props: { color: "green", variant: "contained" },
            style: {
              color: palette.common.white,
              borderColor: palette.success[600],
              backgroundColor: palette.success[600],
              "&:hover": {
                borderColor: palette.success[450],
                backgroundColor: palette.success[450],
              },
              "&:disabled": {
                backgroundColor: palette.success[150],
                color: palette.common.white,
                borderColor: palette.success[150],
              },

              "&:focus": {
                borderColor: palette.success[600],
              },
            },
          },
          /**
           * White Button
           */
          {
            props: { color: "white" },
            style: {
              "&.MuiButtonGroup-grouped": {
                "&:hover": {
                  color: palette.primary.main,
                },
              },
            },
          },
        ],
      },
      MuiIcon: {
        defaultProps: {
          baseClassName: "emt-icons",
        },
        styleOverrides: {
          root: {
            boxSizing: "content-box",
            padding: 3,
            fontSize: "14px",
          },
        },
        variants: [
          {
            props: { fontSize: "small" },
            style: {
              fontSize: `12px`,
            },
          },
          {
            props: { fontSize: "medium" },
            style: {
              fontSize: `14px`,
            },
          },
          {
            props: { fontSize: "large" },
            style: {
              fontSize: `16px`,
            },
          },
        ],
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            border: `1px solid ${palette.greyGradient[100]}`,
            overflow: "hidden",
            borderRadius: 14,
            backgroundColor: palette.common.white,
            fontSize: "14px",
            fontWeight: "bold",
            maxHeight: "50px",
            "&.select-icon": {
              borderLeft: `0px`,
              borderRadius: "0 18px 18px 0",
              "&.Mui-focused": {
                borderColor: palette.greyGradient[100],
                "&.Mui-error": {
                  borderColor: palette.error[450],
                },
              },
            },
            "&:before": {
              borderColor: "transparent !important",
              border: 0,
            },
            "&:after": {
              borderColor: "transparent !important",
              border: 0,
            },
            "& input": {
              paddingTop: "25px",
              paddingBottom: "13px",
            },
            "&:hover": {
              backgroundColor: palette.common.white,
            },
            "&:focus": {
              backgroundColor: palette.common.white,
            },
            "&:disabled": {
              backgroundColor: palette.common.white,
            },
            "& .MuiSelect-select": {
              background: `${palette.common.white} !important`,
            },
            "&.Mui-disabled": {
              background: "inherit",
            },
            "& .Mui-disabled": {
              background: "white !important",
            },
            "&.Mui-focused, &.no-empty": {
              backgroundColor: palette.common.white,
              borderColor: palette.greyGradient[250],
              "&.select-icon": {
                borderColor: palette.greyGradient[100],
              },
            },
            "& .MuiInputAdornment-positionStart .emt-icons": {
              position: "relative",
              top: "-3px",
            },
            [theme.breakpoints.down("md")]: {
              "& .MuiInputAdornment-positionEnd .emt-icons": {
                position: "relative",
              },
            },
            "&.Mui-error": {
              borderColor: `${palette.error[450]} !important`,
              color: palette.error[450],
            },
            "&.MuiAutocomplete-inputRoot": {
              "& input": {
                top: "-6px",
                position: "relative",
              },
              "& .MuiInputAdornment-positionEnd .emt-icons": {
                position: "relative",
                top: "-10px",
                right: "-5px",
              },
            },
            "& .emt-icons": {
              color: palette.greyGradient[550],
              "&.force-right": {
                right: "-32px !important",
              },
            },
          },
        },
        variants: [
          {
            props: { color: "success" },
            style: {
              border: `1px solid ${palette.success.main} !important`,
              color: palette.success.main,
              "&.Mui-focused": {
                borderColor: `${palette.success.main} !important`,
              },
              "&.select-icon": {
                "&.Mui-focused": {
                  borderColor: `${palette.success.main} !important`,
                },
              },
            },
          },
          {
            props: { color: "green" },
            style: {
              border: `1px solid ${palette.success[600]} !important`,
              color: palette.success[600],
              "&.Mui-focused": {
                borderColor: `${palette.success[600]} !important`,
              },
              "&.select-icon": {
                "&.Mui-focused": {
                  borderColor: `${palette.success[600]} !important`,
                },
              },
            },
          },
        ],
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            "&.no-empty .MuiFilledInput-root": {
              backgroundColor: palette.common.white,
              borderColor: palette.greyGradient[250],
              "&.select-icon": {
                borderColor: palette.greyGradient[100],
              },
            },
            "&.date-type": {
              "& .MuiInputLabel-root": {
                fontSize: "14px",
                top: "-1px",
                transform: "translate(12px,7px) scale(0.75)",
              },
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            marginLeft: 0,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "14px",
            color: palette.greyGradient.light,
            "&.Mui-focused": {
              fontSize: "14px",
              top: "-4px",
              color: palette.greyGradient.light,
            },
            "&.MuiInputLabel-shrink": {
              fontSize: "14px",
              top: "-1px",
              color: palette.greyGradient.light,
            },
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            marginLeft: 0,
            marginTop: 1,
            color: palette.greyGradient[250],
          },
        },
        variants: [
          {
            props: { color: "success" },
            style: {
              color: palette.success.main,
            },
          },
          {
            props: { color: "green" },
            style: {
              color: palette.success[600],
            },
          },
          {
            props: { color: "error" },
            style: {
              color: palette.error.main,
            },
          },
          {
            props: { color: "disabled" },
            style: {
              color: palette.greyGradient[150],
            },
          },
        ],
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            background: palette.greyGradient[50],
            padding: 5,
            "&.MuiAutocomplete-option": {
              padding: "5px !important",
              background: palette.greyGradient[50],
            },
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            padding: 0,
            background: palette.greyGradient[50],
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "12px",
          },
        },
        variants: [
          {
            props: { variant: "emt" },
            style: {
              "&.MuiPaper-emt": {
                padding: "40px 30px",
                borderRadius: "18px",
                userSelect: "none",
                "&:hover": {
                  cursor: "pointer",
                  boxShadow: "0px 0px 5px 1px rgba(83,83,83,0.10)",
                },
                [theme.breakpoints.down("md")]: {
                  padding: "30px 5px",
                },
              },
            },
          },
          {
            props: { variant: "sidenav" },
            style: {
              "&.MuiPaper-sidenav": {
                display: "flex",
                flexDirection: "column",
                rowGap: "5px",
                padding: "16px",
                borderRadius: "18px",
                userSelect: "none",
              },
            },
          },
          {
            props: { variant: "information" },
            style: {
              padding: "16px",
              borderRadius: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              gap: "15px",
              background: theme.palette.common.white,
              "& img": {
                alignSelf: "center",
              },
            },
          },
        ],
      },
      MuiPopover: {
        styleOverrides: {
          root: {
            boxShadow: "0 !important",
          },
          paper: {
            boxShadow: "0px 0px 3px -1px rgba(0,0,0,0.57)",
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            padding: 0,
            "& .emt-blank": {
              border: `2px solid ${palette.primary.main}`,
              background: "transparent",
              color: "transparent",
            },
            "& .emt-tick": {
              background: palette.primary.main,
              color: palette.common.white,
            },
            "& .emt-minus": {
              background: palette.primary.main,
              color: palette.common.white,
            },
            "&.Mui-disabled": {
              "& .emt-icons": {
                borderColor: "transparent",
                background: lighten(palette.primary.main, 0.8),
                color: palette.common.white,
              },
              "& .emt-blank": {
                borderColor: palette.greyGradient[100],
                background: "transparent",
              },
            },
          },
        },
        variants: [
          {
            props: { size: "medium" },
            style: {
              "& .emt-icons": {
                border: `2px solid ${palette.primary.main}`,
                padding: "6px",
                borderRadius: "10px",
              },
            },
          },
          {
            props: { size: "small" },
            style: {
              "& .emt-icons": {
                border: `2px solid ${palette.primary.main}`,
                padding: "2px",
                borderRadius: "6px",
              },
            },
          },
        ],
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: 0,
            backgroundColor: "0 0 0",
            "& .MuiSvgIcon-root": {
              border: `2px solid ${palette.primary.main}`,
              color: palette.common.white,
              borderRadius: "100%",
            },
            "&.Mui-checked": {
              "& .MuiSvgIcon-root": {
                color: palette.common.white,
                background: palette.primary.main,
              },
            },
            "&.Mui-disabled": {
              "& .MuiSvgIcon-root": {
                borderColor: palette.greyGradient[100],
                background: palette.common.white,
              },
              "&.Mui-checked": {
                "& .MuiSvgIcon-root": {
                  background: palette.greyGradient[100],
                },
              },
            },
          },
        },
        variants: [
          {
            props: { size: "medium" },
            style: {
              "& .emt-icons": {
                padding: "6px",
                borderRadius: "10px",
              },
            },
          },
          {
            props: { size: "small" },
            style: {
              "& .emt-icons": {
                padding: "2px",
                borderRadius: "6px",
              },
            },
          },
          {
            props: { color: "greyGradient" },
            style: {
              "& .MuiSvgIcon-root": {
                borderColor: palette.greyGradient[100],
              },
              "&.Mui-checked": {
                "& .MuiSvgIcon-root": {
                  borderColor: palette.primary.main,
                },
              },
            },
          },
        ],
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 50,
            height: 28,
            padding: 0,
            "& .MuiSwitch-switchBase": {
              padding: 0,
              margin: 3,
              transitionDuration: "300ms",
              "& .emt-icons": {
                marginTop: -1,
                background: palette.common.white,
                borderRadius: "100%",
                fontSize: "12px",
                padding: "6px",
                color: palette.greyGradient[100],
              },
              "&.Mui-checked": {
                transform: "translateX(22px)",
                color: palette.common.white,
                "& .emt-icons": {
                  color: palette.primary.main,
                },
                "& + .MuiSwitch-track": {
                  backgroundColor: palette.primary.main,
                  opacity: 1,
                  border: 0,
                },
                "&.Mui-disabled + .MuiSwitch-track": {
                  opacity: 0.5,
                },
              },
              "&.Mui-focusVisible .MuiSwitch-thumb": {
                color: palette.primary.main,
                border: `6px solid ${palette.common.white}`,
              },
              "&.Mui-disabled .MuiSwitch-thumb": {
                color: palette.common.white,
              },
              "&.Mui-disabled + .MuiSwitch-track": {
                opacity: 0.7,
              },
            },
            "& .MuiSwitch-thumb": {
              boxSizing: "border-box",
              width: 22,
              height: 22,
            },
            "& .MuiSwitch-track": {
              borderRadius: 26 / 2,
              backgroundColor: palette.greyGradient[100],
              opacity: 1,
            },
          },
        },
        variants: [
          {
            props: { size: "small" },
            style: {
              width: 42,
              height: 24,
              "& .MuiSwitch-switchBase": {
                margin: 2.4,
                "& .emt-icons": {
                  marginTop: -0.1,
                  fontSize: "7px",
                  padding: "6px",
                },
                "&.Mui-checked": {
                  transform: "translateX(18px)",
                },
              },
              "& .MuiSwitch-thumb": {
                width: 19,
                height: 19,
              },
            },
          },
        ],
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontSize: "0.75rem",
            fontWeight: "bold",
            height: "28px",
            "& svg, & .emt-icons": {
              position: "relative",
            },
            "& .MuiChip-avatar": {
              width: "19px",
              height: "19px",
            },
            "& svg": {
              marginLeft: "5px",
            },
          },
        },
        variants: [
          {
            props: { size: "small" },
            style: {
              fontSize: "10px",
              height: "21px",
              "& .emt-icons": {
                fontSize: "10px",
              },
            },
          },
          {
            props: { color: "pink" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.pink.dark,
                },
              },
            },
          },
          {
            props: { color: "purple" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.purple.dark,
                },
              },
            },
          },
          {
            props: { color: "grey" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.greyGradient.dark,
                },
              },
            },
          },
          {
            props: { color: "primarylight" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.primarylight.dark,
                },
              },
            },
          },
          {
            props: { color: "secondarylight" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.secondarylight.dark,
                },
              },
            },
          },
          {
            props: { color: "successlight" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.successlight.dark,
                },
              },
            },
          },
          {
            props: { color: "errorlight" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.errorlight.dark,
                },
              },
            },
          },
          {
            props: { color: "infolight" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.infolight.dark,
                },
              },
            },
          },
          {
            props: { color: "pinklight" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.pinklight.dark,
                },
              },
            },
          },
          {
            props: { color: "greylight" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.greylight.dark,
                },
              },
            },
          },
          {
            props: { color: "purplelight" },
            style: {
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.purplelight.dark,
                },
              },
            },
          },
          {
            props: { color: "transparent" },
            style: {
              color: "white",
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.transparent.dark,
                },
              },
            },
          },
          {
            props: { color: "primarywhite" },
            style: {
              border: `1px solid ${palette.greyGradient[150]}`,
              "&.MuiChip-clickable": {
                "&:hover": {
                  background: palette.primary[50],
                  color: palette.primary.main,
                  borderColor: palette.primary.main,
                },
              },
              "&.active": {
                background: palette.primary[50],
                color: palette.primary.main,
                borderColor: palette.primary.main,
              },
            },
          },
          {
            props: { variant: "disabled" },
            style: {
              "& .MuiChip-label": {
                textDecoration: "line-through",
              },
            },
          },
          {
            props: { color: "tovalidate" },
          },
          {
            props: { color: "pending" },
          },
          {
            props: { color: "accepted" },
          },
          {
            props: { color: "reserved" },
          },
          {
            props: { color: "refused" },
          },
          {
            props: { color: "over" },
          },
          {
            props: { color: "coming" },
          },
          {
            props: { color: "cancelled" },
          },
          {
            props: { color: "sent" },
          },
        ],
      },
      MuiCard: {
        styleOverrides: {
          root: {
            "&.MuiPaper-inlineOutlined": {
              border: `1px solid ${palette.greyGradient[100]}`,
            },
            "&.active": {
              background: lighten(palette.primary[50] as string, 0.8),
              borderColor: palette.primary[50],
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: "18px",
            background: palette.common.white,
            position: "relative",
            overflow: "hidden",
            alignItems: "center",
            minHeight: "45px",
            lineHeight: 0,
            "& .MuiAlert-action": {
              color: palette.greyGradient.main,
            },
          },
        },
        variants: [
          {
            props: { severity: "info" },
            style: {
              color: palette.greyGradient[250],
              "& .MuiAlert-icon": {
                color: palette.greyGradient[250],
              },
            },
          },
          {
            props: { severity: "success" },
            style: {
              color: palette.success.light,
            },
          },
          {
            props: { severity: "error" },
            style: {
              color: palette.error.light,
            },
          },
          {
            props: { severity: "warning" },
            style: {
              color: palette.warning.light,
            },
          },
        ],
      },
      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            "& p": {
              color: palette.primary.main,
            },
            "& a": {
              color: palette.greyGradient[400],
              textDecoration: "none",
              display: "flex",
              alignItems: "baseline",
              gap: "4px",
              "&:hover": {
                color: palette.primary.main,
              },
            },
            "& .MuiButtonBase-root": {
              color: palette.greyGradient[550],
              background: "transparent",
              "&:hover": {
                color: palette.primary.main,
                background: "transparent",
              },
            },
          },
        },
        variants: [
          {
            props: { variant: "white" },
            style: {
              color: palette.common.white,
              "& p": {
                color: palette.common.white,
                fontWeight: "bold",
              },
              "& a": {
                color: palette.common.white,
                "&:hover": {
                  color: palette.common.white,
                },
              },
              "& .MuiButtonBase-root": {
                color: palette.common.white,
                "&:hover": {
                  color: palette.common.white,
                },
              },
            },
          },
        ],
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: "40px",
            height: 8,
          },
        },
        variants: [
          {
            props: { color: "success" },
            style: {
              background: palette.common.white,
              "& .MuiLinearProgress-bar": {
                background: palette.success[600],
              },
            },
          },
          {
            props: { color: "primary" },
            style: {
              background: palette.greyGradient[150],
              "& .MuiLinearProgress-bar": {
                background: palette.primary.main,
              },
            },
          },
        ],
      },
    },
  }) as any;

// @ts-ignore
export const defaultTheme = generateTheme(palette);
